import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { QtfPerEmployeeAllocation } from 'redux/qtf/qtfTypes'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import FormattingUtils from 'shared/FormattingUtils'
import { qtfActions } from 'redux/qtf/qtfSlice'
import palette from 'core-system/Themes/palette'
import {
  Alert as AlertIcon,
  Success as SuccessIcon,
} from 'core-system/Icons/Misc'
import qtfService from 'redux/qtf/qtfService'
import { AppState } from 'redux/config/store'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import * as FlexIcons from 'core-system/Icons/Flex'
import TransitParkingEmployerContribution from 'features/Programs/Editing/Components/TransitParkingEmployerContribution'
import styled from 'styled-components'
import PrimaryButton from 'core-system/Button/PrimaryButton'

const AllocationContainer = styled.div`
  margin-bottom: 1rem;
  border-radius: 0.35rem;
  padding: 0.5rem;
  //background-color: ${palette.grey.grey100};
`

const InfoContainer = styled.div`
  padding: 0.5rem;
  background-color: ${palette.accent.blue100};
  border-radius: 0.35rem;
  margin-bottom: 1rem;
`

const MessageMainContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
`

const MessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-radius: 0.35rem;
  padding: 0.75rem;
  background-color: ${palette.grey.grey200};
`

const allocationHasChanged = (
  originalAmount: number | null,
  currentAmount: number
) => {
  if (
    (!originalAmount && currentAmount > 0) ||
    originalAmount !== currentAmount
  ) {
    return true
  }
  return false
}

const renderStatusMessage = (
  type: 'Transit' | 'Parking',
  status: 'success' | 'error'
) => {
  return (
    <MessageMainContainer>
      {status === 'success' && (
        <MessageContainer>
          <SuccessIcon color={palette.success.green600} />
          <Text variant='action4'>{`${type} Allocation Update Successful`}</Text>
        </MessageContainer>
      )}
      {status === 'error' && (
        <MessageContainer>
          <AlertIcon color={palette.error.red500} />
          <Text variant='action4'>{`${type} Allocation Update Failed`}</Text>
        </MessageContainer>
      )}
    </MessageMainContainer>
  )
}

interface EditEmployeeAllocationModalProps {
  allocation: QtfPerEmployeeAllocation
  onCancel: () => void
}

const EditEmployeeAllocationModal = React.memo(
  (props: EditEmployeeAllocationModalProps) => {
    const { allocation, onCancel } = props
    const dispatch = useDispatch()

    const { irsTaxLimit } = useSelector(
      (state: AppState) => state.application.properties
    )

    const maxEmployeeAllocation = {
      transit:
        irsTaxLimit -
        (allocation.deductions.find(
          (deduction) => deduction.type === 'COMMUTER_TRANSIT'
        )?.employerContribution || 0),
      parking:
        irsTaxLimit -
        (allocation.deductions.find(
          (deduction) => deduction.type === 'COMMUTER_PARKING'
        )?.employerContribution || 0),
    }

    const transitAllocation = allocation.deductions.find(
      (deduction) => deduction.type === 'COMMUTER_TRANSIT'
    )
    const parkingAllocation = allocation.deductions.find(
      (deduction) => deduction.type === 'COMMUTER_PARKING'
    )

    const [currentTransitAmount, setCurrentTransitAmount] = useState(
      transitAllocation?.employeeDeduction
        ? transitAllocation.employeeDeduction
        : 0
    )
    const [currentParkingAmount, setCurrentParkingAmount] = useState(
      parkingAllocation?.employeeDeduction
        ? parkingAllocation.employeeDeduction
        : 0
    )
    const [showMessage, setShowMessage] = useState(false)
    const [parkingStatus, setParkingStatus] = useState<
      null | 'success' | 'error'
    >(null)
    const [transitStatus, setTransitStatus] = useState<
      null | 'success' | 'error'
    >(null)

    const originalAllocation = {
      transit: transitAllocation?.employeeDeduction,
      parking: parkingAllocation?.employeeDeduction,
    }

    const handleTransitAllocationChange = (amount: number) => {
      setCurrentTransitAmount(amount * 100)
    }

    const handleParkingAllocationChange = (amount: number) => {
      setCurrentParkingAmount(amount * 100)
    }

    const createAllocation = async (
      currentAmount: number,
      benefitId: string,
      employeeId: string,
      setStatus: (status: null | 'success' | 'error') => void
    ) => {
      try {
        const result = await qtfService.createEmployeeAllocation({
          employeeDeduction: currentAmount,
          benefit: benefitId,
          employee: employeeId,
        })
        setStatus('success')
        dispatch(qtfActions.createEmployeeAllocationSuccess(result.data))
      } catch (_error) {
        setStatus('error')
      }
    }

    const updateAllocation = async (
      allocationId: string,
      currentAmount: number,
      benefitId: string,
      employeeId: string,
      setStatus: (status: null | 'success' | 'error') => void
    ) => {
      try {
        const result = await qtfService.updateEmployeeAllocation({
          allocationId: allocationId,
          updatedAllocation: {
            employeeDeduction: currentAmount,
            benefit: benefitId,
            employee: employeeId,
          },
        })
        setStatus('success')
        dispatch(qtfActions.updateEmployeeAllocationSuccess(result.data))
      } catch (_error) {
        setStatus('error')
      }
    }

    const handleSubmit = async () => {
      if (
        allocationHasChanged(originalAllocation.transit, currentTransitAmount)
      ) {
        if (originalAllocation.transit) {
          await updateAllocation(
            transitAllocation.id,
            currentTransitAmount,
            transitAllocation.benefitId,
            allocation.employees_id,
            setTransitStatus
          )
        } else {
          await createAllocation(
            currentTransitAmount,
            transitAllocation.benefitId,
            allocation.employees_id,
            setTransitStatus
          )
        }
      }
      if (
        allocationHasChanged(originalAllocation.parking, currentParkingAmount)
      ) {
        if (originalAllocation.parking) {
          await updateAllocation(
            parkingAllocation.id,
            currentParkingAmount,
            parkingAllocation.benefitId,
            allocation.employees_id,
            setParkingStatus
          )
        } else {
          await createAllocation(
            currentParkingAmount,
            parkingAllocation.benefitId,
            allocation.employees_id,
            setParkingStatus
          )
        }
      }
      setShowMessage(true)
    }

    return (
      <div>
        <div>
          <FlexContainer flexDirection='column' width='60%'>
            <div>
              <InfoContainer>
                <Text variant='body2' color={palette.accent.blue700}>
                  Enter the new amount for the commuter's monthly allocation.
                  This change will be effective starting next month.
                </Text>
              </InfoContainer>
              <AllocationContainer>
                <TransitParkingEmployerContribution
                  type='Transit'
                  title='Allocation'
                  icon={
                    <FlexIcons.Transit
                      width={32}
                      height={32}
                      color={palette.white}
                    />
                  }
                  canEdit={true}
                  contributionAmount={currentTransitAmount}
                  onContributionChange={handleTransitAllocationChange}
                  maxContribution={irsTaxLimit}
                  active={true}
                />
              </AllocationContainer>
              <AllocationContainer>
                <TransitParkingEmployerContribution
                  type='Parking'
                  title='Allocation'
                  icon={
                    <FlexIcons.Parking
                      width={32}
                      height={32}
                      color={palette.white}
                    />
                  }
                  canEdit={true}
                  contributionAmount={currentParkingAmount}
                  onContributionChange={handleParkingAllocationChange}
                  maxContribution={irsTaxLimit}
                  active={true}
                />
              </AllocationContainer>
            </div>
            {showMessage ? (
              <div>
                {renderStatusMessage('Transit', transitStatus)}
                {renderStatusMessage('Parking', parkingStatus)}
              </div>
            ) : null}
          </FlexContainer>
          <FlexContainer justifyContent='flex-end'>
            <FlexContainer flexDirection='row' justifyContent='space-between'>
              <SecondaryButton
                onClick={onCancel}
                style={{ marginRight: '1rem' }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={() => handleSubmit()}
                disabled={
                  (!allocationHasChanged(
                    originalAllocation.transit,
                    currentTransitAmount
                  ) &&
                    !allocationHasChanged(
                      originalAllocation.parking,
                      currentParkingAmount
                    )) ||
                  currentTransitAmount > maxEmployeeAllocation.transit ||
                  currentParkingAmount > maxEmployeeAllocation.parking
                }
              >
                Update Allocation
              </PrimaryButton>
            </FlexContainer>
          </FlexContainer>
        </div>
      </div>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  EditEmployeeAllocationModal.displayName = 'EditEmployeeAllocationModal'
}

export default EditEmployeeAllocationModal
